import type { ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { BarList } from "../components/BarList";
import { DataTable } from "../components/DataTable";
import { IssueList } from "../components/IssueList";
import { MonthBars } from "../components/MonthBars";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import type { Issue } from "../lib/api";
import { day, money, num, pct, usd } from "../lib/format";
import { useApi } from "../lib/useApi";
import { SalesFlavours } from "./SalesFlavours";
import { SalesYears } from "./SalesYears";

interface Channel { code: string; name: string; region: string; amount_usd: number; cases: number; rows: number }
interface Sku { sku: string; name: string; cases: number; us_cases: number; non_us_cases: number; us_amount_usd: number | null }
interface Cust { id: number; name: string; channel: string; channel_name: string; amount_usd: number | null; cases: number | null; invoices: number; last_sale: string | null; share: number }
export interface InvoiceRow {
  channel: string; channel_name: string; invoice_no: string; customer_id: number | null; customer: string | null;
  date: string | null; market: string | null; currency: string; amount: number | null; amount_usd: number | null;
  line_cases: number | null; stated_cases: number | null; lines: number;
}

const TABS = [
  { id: "channels", label: "Channels" },
  { id: "years", label: "Year over year" },
  { id: "flavours", label: "By flavour" },
  { id: "products", label: "Products" },
  { id: "customers", label: "Customers" },
  { id: "invoices", label: "Invoices" },
];

export function Sales() {
  const [tab, setTab] = useTab(TABS);
  return (
    <>
      <PageHeader title="Sales" sub="From Zach's sales report (all channels), his gross sales workbook (every year) and MHW's US detail report." />
      <Tabs tabs={TABS} current={tab} onChange={setTab} />
      {tab === "channels" && <Channels />}
      {tab === "years" && <SalesYears />}
      {tab === "flavours" && <SalesFlavours />}
      {tab === "products" && <Products />}
      {tab === "customers" && <CustomersTab />}
      {tab === "invoices" && <Invoices />}
    </>
  );
}

function Channels() {
  const ch = useApi<Channel[]>("/sales/by-channel");
  const [params, setParams] = useSearchParams();
  const channel = params.get("channel") ?? "";
  const monthly = useApi<{ months: { month: string; amount_usd: number }[]; undated: { count: number; amount_usd: number } }>(
    `/sales/monthly${channel ? `?channel=${channel}` : ""}`);
  if (ch.error) return <ErrorBox error={ch.error} onRetry={ch.reload} />;
  if (!ch.data) return <Loading />;
  const total = ch.data.reduce((s, c) => s + (c.amount_usd ?? 0), 0);
  return (
    <div className="two-up">
      <Card title="Revenue by channel">
        <BarList bars={ch.data.map((c) => ({ key: c.code, label: c.name, value: c.amount_usd, valueLabel: usd(c.amount_usd),
          meta: `${pct(c.amount_usd / total)} · ${num(c.cases)} cs`, href: `/sales?tab=invoices&channel=${c.code}` }))} />
      </Card>
      <Card title="Revenue by month" actions={
        <select aria-label="Channel" value={channel} onChange={(e) => {
          const n = new URLSearchParams(params); if (e.target.value) n.set("channel", e.target.value); else n.delete("channel"); setParams(n, { replace: true });
        }}>
          <option value="">All channels</option>
          {ch.data.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>}>
        {monthly.data ? <MonthBars title="Revenue" points={monthly.data.months.map((m) => ({ month: m.month, value: m.amount_usd, label: usd(m.amount_usd) }))} /> : <Loading />}
        {monthly.data && monthly.data.undated.count > 0 && (
          <p className="note muted">{monthly.data.undated.count} undated invoice(s), {usd(monthly.data.undated.amount_usd)}, are not in any month.</p>
        )}
      </Card>
    </div>
  );
}

function Products() {
  const { data, error, reload } = useApi<Sku[]>("/sales/by-sku");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return (
    <>
      <Card title="Cases sold by product, all channels">
        <BarList bars={data.map((s) => ({ key: s.sku, label: s.name.replace("Shipwreck ", ""), value: s.cases,
          valueLabel: `${num(s.cases)} cs`, href: `/products/${s.sku}` }))} />
      </Card>
      <Card>
        <DataTable<Sku> rows={data} rowKey={(r) => r.sku} href={(r) => `/products/${r.sku}`} testId="sku-table"
          initialSort={{ key: "cases", dir: "desc" }} columns={[
            { key: "name", label: "Product" },
            { key: "us_cases", label: "US cases", numeric: true, render: (r) => num(r.us_cases) },
            { key: "non_us_cases", label: "Non-US cases", numeric: true, render: (r) => num(r.non_us_cases) },
            { key: "cases", label: "Total cases", numeric: true, render: (r) => <strong>{num(r.cases)}</strong> },
            { key: "us_amount_usd", label: "US revenue", numeric: true, render: (r) => usd(r.us_amount_usd) },
          ]} />
        <p className="note muted">Non-US invoices give cases per flavour but not money per flavour, so revenue by product exists for the US only.</p>
      </Card>
    </>
  );
}

function CustomersTab() {
  const { data, error, reload } = useApi<{ customers: Cust[] }>("/sales/by-customer?limit=500");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return <Card><CustomerTable rows={data.customers} /></Card>;
}

export function CustomerTable({ rows }: { rows: Cust[] }) {
  return (
    <DataTable<Cust> rows={rows} rowKey={(r) => r.id} href={(r) => `/customers/${r.id}`} testId="customer-table"
      initialSort={{ key: "amount_usd", dir: "desc" }} columns={[
        { key: "name", label: "Customer", render: (r) => <Link to={`/customers/${r.id}`}>{r.name}</Link> },
        { key: "channel_name", label: "Channel" },
        { key: "amount_usd", label: "Revenue", numeric: true, render: (r) => usd(r.amount_usd) },
        { key: "share", label: "Share", numeric: true, render: (r) => pct(r.share) },
        { key: "cases", label: "Cases", numeric: true, render: (r) => num(r.cases) },
        { key: "invoices", label: "Invoices", numeric: true },
        { key: "last_sale", label: "Last sale", render: (r) => day(r.last_sale) },
      ]} />
  );
}

function Invoices() {
  const [params, setParams] = useSearchParams();
  const channel = params.get("channel") ?? "";
  const { data, error, reload } = useApi<InvoiceRow[]>(`/sales/invoices${channel ? `?channel=${channel}` : ""}`);
  const ch = useApi<Channel[]>("/sales/by-channel");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  return (
    <Card>
      {!data ? <Loading /> : (
        <InvoiceTable rows={data} toolbar={
          <select aria-label="Channel" value={channel} onChange={(e) => {
            const n = new URLSearchParams(params); if (e.target.value) n.set("channel", e.target.value); else n.delete("channel"); setParams(n, { replace: true });
          }}>
            <option value="">All channels</option>
            {(ch.data ?? []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>} />
      )}
    </Card>
  );
}

export function InvoiceTable({ rows, toolbar }: { rows: InvoiceRow[]; toolbar?: ReactNode }) {
  return (
    <DataTable<InvoiceRow> rows={rows} rowKey={(r) => `${r.channel}-${r.invoice_no}-${r.customer_id}`} toolbar={toolbar}
      testId="invoice-table" filterPlaceholder="Filter by invoice, customer, market…"
      href={(r) => `/sales/invoices/${r.channel}/${encodeURIComponent(r.invoice_no)}`}
      columns={[
        { key: "invoice_no", label: "Invoice", render: (r) => <Link to={`/sales/invoices/${r.channel}/${encodeURIComponent(r.invoice_no)}`}>#{r.invoice_no}</Link> },
        { key: "date", label: "Date", render: (r) => (r.date ? day(r.date) : <span className="muted">undated</span>) },
        { key: "customer", label: "Customer" },
        { key: "channel_name", label: "Channel" },
        { key: "market", label: "Market", render: (r) => r.market ?? "—" },
        { key: "cases", label: "Cases", numeric: true, value: (r) => r.stated_cases ?? r.line_cases, render: (r) => num(r.stated_cases ?? r.line_cases) },
        { key: "amount", label: "Amount", numeric: true, render: (r) => money(r.amount, r.currency, 2) },
        { key: "amount_usd", label: "USD", numeric: true, render: (r) => usd(r.amount_usd) },
      ]} />
  );
}

interface InvoiceDetailT {
  channel: string; invoice_no: string; customer: { id: number; name: string } | null; date: string | null; market: string | null;
  currency: string; amount: number | null; amount_usd: number | null; fx_rate_to_usd: number | null; includes_tax: boolean | null;
  stated_cases: number | null; run_id: number;
  lines: { line_no: number; sku: string | null; name: string | null; cases: number; unit_price: number | null; amount_usd: number | null; adj_code: string | null; date: string | null }[];
  issues: { id: number; severity: Issue["severity"]; summary: string }[];
}

export function InvoiceDetail() {
  const { channel, invoice } = useParams();
  const { data, error, reload } = useApi<InvoiceDetailT>(`/sales/invoices/${channel}/${encodeURIComponent(invoice ?? "")}`);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const lineCases = data.lines.reduce((s, l) => s + (l.cases ?? 0), 0);
  return (
    <>
      <PageHeader title={`Invoice #${data.invoice_no}`} sub={data.customer?.name}
        crumbs={[{ label: "Sales", to: "/sales" }, { label: "Invoices", to: "/sales?tab=invoices" }]} />
      <div className="two-up">
        <Card title="Details">
          <Facts items={[
            ["Customer", data.customer ? <Link to={`/customers/${data.customer.id}`}>{data.customer.name}</Link> : "—"],
            ["Channel", data.channel], ["Date", data.date ? day(data.date) : "undated"], ["Market", data.market],
            ["Amount", money(data.amount, data.currency)], ["In USD", usd(data.amount_usd)],
            ["FX to USD", data.currency === "USD" ? "—" : num(data.fx_rate_to_usd, 6)],
            ["Includes VAT", data.includes_tax == null ? "unknown" : data.includes_tax ? "yes" : "no"],
            ["Cases stated", num(data.stated_cases)], ["Cases by flavour", num(lineCases)],
            ["Source", <Link to={`/imports/${data.run_id}`}>import #{data.run_id}</Link>],
          ]} />
        </Card>
        <Card title="Issues on this invoice">
          <IssueList issues={data.issues.map((i) => ({ ...i, check: "", detail: {}, detected_at: "", resolved_at: null, source: null, run_id: null }))} compact />
        </Card>
      </div>
      <Card title="Lines">
        <DataTable rows={data.lines} rowKey={(l) => `${l.line_no}-${l.sku}-${l.date}`} filterPlaceholder={false}
          href={(l) => (l.sku ? `/products/${l.sku}` : null)} columns={[
            { key: "name", label: "Product", render: (l) => l.name ?? "(unmatched)" },
            { key: "cases", label: "Cases", numeric: true, render: (l) => num(l.cases, 2) },
            { key: "unit_price", label: "Unit price", numeric: true, render: (l) => (l.unit_price == null ? "—" : money(l.unit_price)) },
            { key: "amount_usd", label: "Amount", numeric: true, render: (l) => (l.amount_usd == null ? "—" : money(l.amount_usd)) },
            { key: "adj_code", label: "Adj.", render: (l) => l.adj_code ?? "" },
          ]} />
      </Card>
    </>
  );
}
